/*!
 * Copyright © 2026 Nail Alliance - North America, Inc.
 * Authors: See snippets/copyright-notices.liquid in the theme.
 * No other use is authorized without a written contract.
 */

// Description grammar and disclosure boundaries ported from svelte-store's
// product-description-accordion.js. Shopify owns product lookup, cards and cart.
const blocks = new Set(['ADDRESS', 'ARTICLE', 'ASIDE', 'BLOCKQUOTE', 'DIV', 'DL', 'DT', 'DD', 'FIGCAPTION', 'FIGURE', 'FOOTER', 'HEADER', 'HR', 'LI', 'MAIN', 'NAV', 'OL', 'P', 'PRE', 'SECTION', 'UL']);
const slugify = value => value.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'section';

export function isAccordionDescription(html) {
  return /^(?:\uFEFF|\s|&nbsp;)*(?:<(?:p|div)\b[^>]*>\s*)?(?:<!--\s*@accordion\s*-->|--accordion--(?=\s|<br|<\/|$))/i.test(html);
}

export function descriptionNodeText(node) {
  if (node.nodeType === 3) return node.nodeValue || '';
  if (node.nodeType === 8) {
    const section = (node.nodeValue || '').match(/^\s*@section\s*:\s*(.+?)\s*$/i);
    const skus = (node.nodeValue || '').match(/^\s*@skus\s*:\s*(.*?)\s*$/i);
    if (section) return `\n# ${section[1].trim()}\n`;
    if (skus) return `\n@skus: ${skus[1].trim()}\n`;
    return '';
  }
  if (node.nodeType !== 1 || ['SCRIPT', 'STYLE'].includes(node.tagName)) return '';
  if (node.tagName === 'BR') return '\n';
  const text = Array.from(node.childNodes, descriptionNodeText).join('');
  if (/^H[1-6]$/.test(node.tagName)) return `\n# ${text.trim()}\n`;
  return blocks.has(node.tagName) ? `\n${text}\n` : text;
}

export function parseDescriptionSections(source) {
  const sections = [];
  let active;
  const text = source.replace(/\u00a0/g, ' ').replace(/\r\n?/g, '\n')
    .replace(/[ \t]+\n/g, '\n').replace(/\n[ \t]+/g, '\n').replace(/\n{3,}/g, '\n\n')
    .trim().replace(/^\s*--accordion--\s*(?:\n|$)/i, '');
  for (const line of text.split('\n')) {
    const heading = line.match(/^\s*#{1,6}\s+(.+?)\s*$/);
    if (heading) {
      active = { title: heading[1].trim(), lines: [] };
      sections.push(active);
    } else {
      if (!active && line.trim()) {
        active = { title: 'Product Description', lines: [] };
        sections.push(active);
      }
      active?.lines.push(line);
    }
  }
  return sections.map(({ title, lines }) => {
    const pairSkus = [];
    const content = lines.filter(line => {
      if (slugify(title) !== 'pair-with') return true;
      const match = line.match(/^\s*(?:@skus|skus|\[skus)\s*:\s*([^\]]*?)\]?\s*$/i);
      if (!match) return true;
      for (const sku of match[1].split(',').map(value => value.trim())) {
        if (/^[a-z0-9][a-z0-9._-]{0,63}$/i.test(sku) && !pairSkus.includes(sku)) pairSkus.push(sku);
      }
      return false;
    }).join('\n').trim();
    return { title, content, pairSkus };
  }).filter(({ title, content }) => title || content);
}

export function shadePage(index, count, size = 36) {
  return Math.min(Math.max(0, Math.floor(index / size)), Math.max(0, Math.ceil(count / size) - 1));
}

let instance = 0;
if (typeof customElements !== 'undefined') {
  class ProductDescription extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      if (!this.dataset.enhanced) {
        // Preserve ordinary rich descriptions exactly, including links and lists.
        if (!isAccordionDescription(this.innerHTML)) return;
        const sections = parseDescriptionSections(Array.from(this.childNodes, descriptionNodeText).join(''));
        if (!sections.length) return;
        this.render(sections);
        this.dataset.enhanced = '';
      }
      this.addEventListener('click', event => {
        const button = event.target.closest('.product-description__trigger');
        if (!button || !this.contains(button)) return;
        const panel = this.querySelector(`#${button.getAttribute('aria-controls')}`);
        const expanded = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(expanded));
        panel.hidden = !expanded;
        if (expanded && panel.dataset.pairSkus) this.loadPairs(panel);
      }, { signal });
      this.addEventListener('keydown', event => {
        const buttons = [...this.querySelectorAll('.product-description__trigger')];
        const index = buttons.indexOf(event.target);
        if (index < 0) return;
        const next = { ArrowDown: (index + 1) % buttons.length, ArrowUp: (index - 1 + buttons.length) % buttons.length, Home: 0, End: buttons.length - 1 }[event.key];
        if (next !== undefined) { event.preventDefault(); buttons[next].focus(); }
      }, { signal });
    }

    disconnectedCallback() { this.controller?.abort(); }

    render(sections) {
      const fragment = document.createDocumentFragment();
      const id = `ProductDescription-${++instance}`;
      sections.forEach((section, index) => {
        const item = document.createElement('section');
        item.className = 'product-description__item';
        const panel = document.createElement('div');
        panel.className = 'product-description__panel';
        panel.id = `${id}-panel-${index}`;
        panel.hidden = index !== 0;
        if (index === 0) {
          item.classList.add('product-description__intro');
        } else {
          const heading = document.createElement('h3');
          const button = document.createElement('button');
          const label = document.createElement('span');
          const icon = document.createElement('span');
          heading.className = 'product-description__heading';
          button.type = 'button';
          button.className = 'product-description__trigger';
          button.id = `${id}-button-${index}`;
          button.setAttribute('aria-expanded', 'false');
          button.setAttribute('aria-controls', panel.id);
          label.textContent = section.title;
          icon.className = 'product-detail-toggle';
          icon.setAttribute('aria-hidden', 'true');
          button.append(label, icon);
          heading.append(button);
          item.append(heading);
          panel.setAttribute('role', 'region');
          panel.setAttribute('aria-labelledby', button.id);
        }
        for (const text of section.content.split(/\n\s*\n/).filter(value => value.trim())) {
          const paragraph = document.createElement('p');
          paragraph.textContent = text.replace(/\s*\n\s*/g, ' ').trim();
          panel.append(paragraph);
        }
        if (section.pairSkus.length) {
          panel.dataset.pairSkus = section.pairSkus.join(',');
          panel.classList.add('product-description__panel--pairs');
          const grid = document.createElement('div');
          grid.className = 'product-description__pairs';
          grid.setAttribute('aria-live', 'polite');
          panel.append(grid);
        }
        item.append(panel);
        fragment.append(item);
      });
      this.replaceChildren(fragment);
      this.querySelectorAll('[data-pair-skus]:not([hidden])').forEach(panel => this.loadPairs(panel));
    }

    async loadPairs(panel) {
      if (panel.dataset.pairState === 'loading' || panel.dataset.pairState === 'loaded') return;
      const grid = panel.querySelector('.product-description__pairs');
      const skus = panel.dataset.pairSkus.split(',');
      panel.dataset.pairState = 'loading';
      grid.textContent = 'Loading paired products…';
      grid.setAttribute('aria-busy', 'true');
      try {
        const cards = [];
        // Shopify's SKU lookup resolves individual full SKUs; combining them as
        // an OR/phrase query can miss variant-SKU matches. Bound parallel reads.
        for (let offset = 0; offset < skus.length; offset += 4) {
          const batches = await Promise.all(skus.slice(offset, offset + 4).map(async sku => {
            const url = new URL(this.dataset.searchUrl || `${window.Shopify?.routes?.root || '/'}search`, window.location.origin);
            url.search = new URLSearchParams({ type: 'product', q: sku, view: 'product-pairs', 'options[unavailable_products]': 'show' }).toString();
            const response = await fetch(url, { signal: this.controller.signal });
            if (!response.ok) throw new Error('Product search failed');
            const result = new DOMParser().parseFromString(await response.text(), 'text/html');
            if (!result.querySelector('[data-paired-results]')) throw new Error('Product template unavailable');
            return [...result.querySelectorAll('[data-paired-product]')];
          }));
          cards.push(...batches.flat());
        }
        const matches = skus.map(sku => cards.find(card => card.dataset.sku === sku)).filter(Boolean);
        grid.replaceChildren(...matches);
        if (matches.length < skus.length) {
          const message = document.createElement('p');
          message.className = 'product-description__pair-message';
          message.textContent = matches.length ? 'Some paired products are not available in this store.' : 'These paired products are not available in this store.';
          grid.append(message);
        }
        panel.dataset.pairState = 'loaded';
      } catch (error) {
        delete panel.dataset.pairState;
        if (error.name !== 'AbortError') grid.textContent = 'Paired products could not be loaded. Close and reopen this section to try again.';
      } finally {
        grid.setAttribute('aria-busy', 'false');
      }
    }
  }

  class ProductColorPicker extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.list = this.querySelector('[data-shade-list]');
      this.shades = [...this.querySelectorAll('[data-shade]')].sort((a, b) => a.dataset.sku.localeCompare(b.dataset.sku, undefined, { numeric: true }));
      if (!this.list || !this.shades.length) return;
      this.list.append(...this.shades);
      this.size = Math.max(1, Number(this.dataset.pageSize) || 36);
      this.page = shadePage(this.shades.findIndex(shade => shade.getAttribute('aria-current') === 'page'), this.shades.length, this.size);
      this.querySelector('[data-shade-previous]').addEventListener('click', () => this.showPage(this.page - 1), { signal });
      this.querySelector('[data-shade-next]').addEventListener('click', () => this.showPage(this.page + 1), { signal });
      const scroll = this.querySelector('[data-shade-scroll]');
      scroll.hidden = false;
      scroll.addEventListener('click', () => this.list.scrollBy({ left: 45, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }), { signal });
      this.querySelector('[data-shade-pagination]').hidden = this.shades.length <= this.size;
      this.showPage(this.page);
    }

    disconnectedCallback() { this.controller?.abort(); }

    showPage(page) {
      this.page = shadePage(page * this.size, this.shades.length, this.size);
      this.shades.forEach((shade, index) => { shade.hidden = Math.floor(index / this.size) !== this.page; });
      this.querySelector('[data-shade-previous]').disabled = this.page === 0;
      this.querySelector('[data-shade-next]').disabled = this.page >= Math.ceil(this.shades.length / this.size) - 1;
      this.querySelector('[data-shade-status]').textContent = `Page ${this.page + 1} of ${Math.ceil(this.shades.length / this.size)}`;
      this.list.scrollLeft = 0;
    }
  }

  if (!customElements.get('product-description')) customElements.define('product-description', ProductDescription);
  if (!customElements.get('product-color-picker')) customElements.define('product-color-picker', ProductColorPicker);
}
